import { Box, Text, IconButton } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { FiEyeOff, FiEye } from 'react-icons/fi'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import type { ItineraryAssignmentDto, AssignmentScheduleInfoDto } from '../../../types/campaign'
import { ItineraryAssignmentCard } from './ItineraryAssignmentCard'

interface ItineraryCellDropZoneProps {
  grantId: string
  dateStr: string
  assignments: ItineraryAssignmentDto[]
  scheduleMap?: Record<string, AssignmentScheduleInfoDto>
  isAvailable?: boolean
  onRemove?: (assignmentId: string) => void
  onToggleAvailability?: (grantId: string, dateStr: string, isAvailable: boolean) => void
}

export function ItineraryCellDropZone({
  grantId,
  dateStr,
  assignments,
  scheduleMap,
  isAvailable = true,
  onRemove,
  onToggleAvailability,
}: ItineraryCellDropZoneProps) {
  const droppableId = `${grantId}:${dateStr}`
  const { isOver, setNodeRef } = useDroppable({
    id: droppableId,
    data: { type: 'cell', grantId, dateStr },
  })

  const bgColor = !isAvailable
    ? 'red.50'
    : isOver
      ? 'accent.soft'
      : 'gray.50'

  const borderColor = !isAvailable
    ? 'red.200'
    : isOver
      ? 'accent'
      : 'gray.200'

  return (
    <Box
      ref={setNodeRef}
      minH="60px"
      bg={bgColor}
      borderRadius="md"
      border="1px solid"
      borderColor={borderColor}
      p={1}
      transition="all 0.15s"
      position="relative"
      {...(!isAvailable && {
        backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 8px, rgba(0,0,0,0.03) 8px, rgba(0,0,0,0.03) 9px)',
      })}
    >
      {/* Availability toggle */}
      <Box position="absolute" top="1px" right="1px" zIndex={1}>
        <Tooltip contentProps={{ fontSize: "xs" }}
          content={isAvailable ? 'Mark unavailable' : 'Mark available'}
          showArrow
          positioning={{
            placement: "top"
          }}
        >
          <IconButton
            aria-label={isAvailable ? 'Mark unavailable' : 'Mark available'}
            size="xs"
            variant="ghost"
            color={isAvailable ? 'gray.300' : 'red.400'}
            _hover={{ color: isAvailable ? 'gray.500' : 'green.500' }}
            onClick={() => onToggleAvailability?.(grantId, dateStr, !isAvailable)}
            minW="18px"
            h="18px"
            fontSize="10px">{isAvailable ? <FiEyeOff /> : <FiEye />}</IconButton>
        </Tooltip>
      </Box>

      <SortableContext
        items={assignments.map(a => a.id)}
        strategy={verticalListSortingStrategy}
      >
        {assignments.map(assignment => (
          <ItineraryAssignmentCard
            key={assignment.id}
            assignment={assignment}
            scheduleInfo={scheduleMap?.[assignment.id]}
            onRemove={onRemove}
          />
        ))}
      </SortableContext>
      {assignments.length === 0 && (
        <Text fontSize="2xs" color={!isAvailable ? 'red.300' : 'gray.400'} textAlign="center" pt={4}>
          {!isAvailable ? 'Unavailable' : 'Drop turf here'}
        </Text>
      )}
    </Box>
  );
}
